// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Printer, XCircle, ExternalLink, Truck, CheckCircle2, Circle, Send, RefreshCw, Download, Image } from 'lucide-react'
import * as api from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { carrierLabel } from '../../lib/carriers'

/**
 * Shipment detail drawer (Sprint 18, BUG-003).
 *
 * Opens when any worklist row is clicked. Works for ALL three shipment types:
 *   - Rx-linked: shows the Rx number + provides a link to open the Rx
 *   - Manual: shows the manually-entered recipient
 *   - Imported: shows the imported recipient (carrier-snapshot)
 *
 * Why a drawer vs a separate page: a page would require a route + back-button
 * dance; the drawer keeps the worklist context underneath (filter state, scroll
 * position) and is the right weight for what staff need — a "look at this one"
 * surface, not a dashboard.
 */
export default function ShipmentDetailDrawer({ shipmentId, onClose, onCancelled }: {
  shipmentId: string
  onClose: () => void
  onCancelled: () => void
}) {
  const navigate = useNavigate()
  const { hasPermission, role } = useAuth()
  const canCancel = hasPermission('shipping.create')
  const isAdmin = role === 'admin'
  const [data, setData] = useState<{ shipment: api.ShipmentDetailRecord; events: api.ShipmentDetailEvent[]; documents: api.ShipmentPodDocument[] } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const loadDetail = () =>
    api.getShipmentDetail(shipmentId)
      .then(setData)
      .catch((e: any) => setError(e?.message || 'Could not load shipment.'))

  useEffect(() => { loadDetail() }, [shipmentId])

  // Imported orders: re-pull from the courier via the import endpoint's refresh
  // path — fills the Rx Ref# (carrier_reference), POD, documents, and costs on
  // an already-imported shipment, then reloads the drawer.
  const [fetchingDocs, setFetchingDocs] = useState(false)
  // POD (Patrick 2026-08-02): pull delivery photos + POD from the courier for THIS order,
  // for any delivered shipment (not just imports). One click = photos in the drawer/POD/PDF.
  const doFetchDocs = async () => {
    setFetchingDocs(true); setError(null)
    try {
      const r = await api.fetchShipmentDocuments(shipmentId)
      if (r.fetched > 0) { await loadDetail() }
      else window.alert(r.detail || 'The courier returned no documents for this order.')
    } catch (e: any) { setError(e?.payload?.message || e?.message || 'Could not fetch documents from the courier.') }
    finally { setFetchingDocs(false) }
  }
  const doRefreshFromCourier = async () => {
    const orderNo = data?.shipment?.carrier_shipment_id
    if (!orderNo) return
    setRefreshing(true); setError(null)
    try {
      await api.refreshShipmentStatus(shipmentId)   // D-SHIP-8: canonical status refresh (shared core)
      await loadDetail()
    } catch (e: any) {
      setError(e?.payload?.message || e?.message || 'Refresh from courier failed.')
    } finally {
      setRefreshing(false)
    }
  }

  // Close on ESC — keyboard-first dismissal is expected for drawers.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const doCancel = async () => {
    if (!data) return
    if (!window.confirm('Cancel this shipment? If the courier has already picked it up, cancellation may be refused.')) return
    setCancelling(true)
    try {
      const res: any = await api.cancelShipment(shipmentId)
      if (res.status === 'cancel_pending') { window.alert(res.message); loadDetail() }   // D-SHIP-3: minutes-scale EDI flip — verify below
      else if (res.status !== 'cancelled') setError(res.message || 'Shipment could not be cancelled.')
      else if (res.carrier_confirmed === false) setError(res.message)  // cancelled locally, courier still live — keep drawer open, show truth
      else { if (res.message) window.alert(res.message); onCancelled() }
    } catch (e: any) {
      setError(e?.payload?.message || e?.message || 'Cancel failed.')
    } finally {
      setCancelling(false)
    }
  }

  const [verifying, setVerifying] = useState(false)
  const doVerifyCancel = async () => {
    setVerifying(true); setError(null)
    try {
      const res = await api.verifyShipmentCancel(shipmentId)
      if (res.cancelled) { window.alert('Cancellation confirmed by the courier.'); onCancelled() }
      else { window.alert(res.message || (res.pending ? 'Still pending at the courier.' : 'Verification result unknown.')); loadDetail() }
    } catch (e: any) {
      setError(e?.payload?.message || e?.message || 'Verify failed.')
    } finally { setVerifying(false) }
  }

  const doDelete = async () => {
    if (!data) return
    if (!window.confirm('Delete this order at the courier? Admin-only, and only valid before the order ships. The carrier result will be shown.')) return
    setDeleting(true); setError(null)
    try {
      const res = await api.deleteOrderAdmin(shipmentId)
      if (res.status !== 'cancelled') { setError(res.message || 'Order could not be deleted.'); return }
      if (res.carrier_confirmed === false) window.alert(res.message || 'Marked deleted in OwlRx; carrier result unconfirmed.')
      else if (res.message) window.alert(res.message)
      onCancelled()
    } catch (e: any) {
      // The backend returns a specific reason with a title + next-step action.
      const p = e?.payload
      if (p?.title || p?.action) {
        const orderRef = p.order_number ? ` (order ${p.order_number})` : ''
        setError(`${p.title || 'Could not delete the order.'}${orderRef}\n\nWhat to do: ${p.action || 'Contact courier dispatch.'}`)
      } else {
        setError(p?.message || p?.detail || e?.message || 'Delete failed.')
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div style={overlay} onClick={onClose} />
      <aside style={drawer} role="dialog" aria-label="Shipment details">
        <header style={hdr}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Shipment details</h2>
          <button type="button" onClick={onClose} style={closeBtn} title="Close (Esc)">
            <X size={16} />
          </button>
        </header>

        {error && <div style={errBox}>{error}</div>}
        {!data && !error && <div style={{ padding: 18, color: 'var(--color-text-muted)' }}>Loading…</div>}

        {data && (
          <div style={body}>
            <ShipmentBody data={data} navigate={navigate} canCancel={canCancel} cancelling={cancelling} doCancel={doCancel} isAdmin={isAdmin} deleting={deleting} doDelete={doDelete} shipmentId={shipmentId} refreshing={refreshing} doRefreshFromCourier={doRefreshFromCourier} fetchingDocs={fetchingDocs} doFetchDocs={doFetchDocs} verifying={verifying} doVerifyCancel={doVerifyCancel} />
          </div>
        )}
      </aside>
    </>
  )
}

function ShipmentBody({ data, navigate, canCancel, cancelling, doCancel, isAdmin, deleting, doDelete, shipmentId, refreshing, doRefreshFromCourier, fetchingDocs, doFetchDocs, verifying, doVerifyCancel }: {
  data: { shipment: api.ShipmentDetailRecord; events: api.ShipmentDetailEvent[]; documents: api.ShipmentPodDocument[] }
  navigate: (path: string) => void
  canCancel: boolean
  cancelling: boolean
  refreshing: boolean
  doRefreshFromCourier: () => void
  fetchingDocs: boolean
  doFetchDocs: () => void
  doCancel: () => Promise<void>
  isAdmin: boolean
  deleting: boolean
  doDelete: () => Promise<void>
  shipmentId: string
  verifying: boolean
  doVerifyCancel: () => Promise<void>
}) {
  const s = data.shipment
  // Money policy (matches BuildPodDocAsync): $ amounts appear ONLY with shipping.config.
  const canSeeCost = useAuth().hasPermission('shipping.config')
  const [reconciling, setReconciling] = useState(false)
  const [reconcileMsg, setReconcileMsg] = useState<string | null>(null)
  const doReconcile = async () => {
    setReconciling(true); setReconcileMsg(null)
    try {
      const r = await api.reconcileCarrierOrder(shipmentId)
      setReconcileMsg(`Courier order #${r.order_number} linked. Cancel/track now target the real order.`)
      doRefreshFromCourier()
    } catch (e: any) { setReconcileMsg(e?.payload?.message || e?.payload?.detail || e?.message || 'Not found yet — try again shortly.') }
    finally { setReconciling(false) }
  }
  const needsReconcile = !s.imported_from_courier_at && !s.carrier_order_confirmed

  const [labelCfg, setLabelCfg] = useState<api.LabelConfig | null>(null)
  const [photoZoom, setPhotoZoom] = useState<string | null>(null)   // POD-4: expand a delivery photo
  useEffect(() => { api.getLabelConfig().then(r => setLabelCfg(r.config)).catch(() => {}) }, [])
  const isImport = !!s.imported_from_courier_at
  const type = isImport ? 'Imported' : s.is_manual ? 'Manual' : 'Rx-linked'
  const canCancelStatus = s.status !== 'delivered' && s.status !== 'cancelled'
  const canResendCancel = s.status === 'cancelled' && !!s.carrier_shipment_id && /^[0-9]+$/.test(s.carrier_shipment_id)
  // Cancelled lockdown: on a cancelled / cancel-pending order the only remaining actions are
  // the cancel-flow ones (verify, re-send cancel, admin delete). Everything operational hides.
  const isCancelledState = s.status === 'cancelled' || s.status === 'cancel_pending'

  return (
    <>
      {/* Header row: identifier + status + type */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {(() => {
            const refs = (s as any).rx_refs as { rx_number: string; rx_id: string }[] | undefined
            const lk = { color: 'var(--color-primary, #2563eb)', textDecoration: 'none', fontWeight: 700 } as const
            if (refs && refs.length > 0) return (
              <span>Rx {refs.map((rf, i) => (
                <span key={rf.rx_id}>
                  {i > 0 && <span style={{ color: 'var(--color-text-muted)' }}>, </span>}
                  <a href={`/rx/${rf.rx_id}?from=shipment&sid=${shipmentId}`} style={lk} title={`Open Rx ${rf.rx_number}`}>{rf.rx_number}</a>
                </span>
              ))}</span>
            )
            if (s.rx_number) return <span>Rx <a href={`/rx/${s.rx_item_id}?from=shipment&sid=${shipmentId}`} style={lk}>{s.rx_number}</a></span>
            if (s.carrier_reference) return `Rx ${s.carrier_reference}`
            return `Order ${s.order_label || s.carrier_shipment_id || s.tracking_number || s.id.slice(0, 8)}`
          })()}
        </div>
        <StatusPill status={s.status} />
        <span style={typeTag}>{type}</span>
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>· {carrierLabel(s.carrier)}</span>
      </div>

      {/* Action row */}
      <div style={actionRow}>
        {/* POD-6 (Patrick 2026-08-02): no label to print once an order is cancelled / cancel-pending */}
        {/* autoclose=1 — see ShipmentsPage: the label tab auto-prints, so it should
            close itself once the job is away rather than pile up on the dispatch machine. */}
        {s.status !== 'cancelled' && s.status !== 'cancel_pending' && (
          <button type="button" style={btn} onClick={() => window.open(`/shipments/${shipmentId}/label?autoclose=1`, '_blank')}>
            <Printer size={14} /> Print label
          </button>
        )}
        {!isCancelledState && s.rx_item_id && (
          <button type="button" style={btn} onClick={() => navigate(`/rx/${s.rx_item_id}`)}>
            <ExternalLink size={14} /> Open Rx
          </button>
        )}
        {!isCancelledState && isImport && s.carrier_shipment_id && (
          <button type="button" style={btn} disabled={refreshing}
                  title="Re-pull this order from the courier (fills Rx Ref#, POD, documents, costs)"
                  onClick={doRefreshFromCourier}>
            <RefreshCw size={14} /> {refreshing ? 'Refreshing…' : 'Refresh from courier'}
          </button>
        )}
        {!isCancelledState && (
          <button type="button" style={btn}
                  title="Download the Proof-of-Delivery / shipment status document"
                  onClick={() => api.openShipmentPodDoc(shipmentId).catch(() => { alert('Could not open the delivery document. Please try again.') })}>
            <Download size={14} /> Download POD
          </button>
        )}
        {s.status === 'delivered' && s.carrier_shipment_id && (
          <button type="button" style={btn} disabled={fetchingDocs}
                  title="Pull the delivery photos + POD from the courier for this order"
                  onClick={doFetchDocs}>
            <Image size={14} /> {fetchingDocs ? 'Fetching…' : 'Get delivery photos'}
          </button>
        )}
        {s.status === 'cancel_pending' && (
          <div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 8, border: '1px solid #d97706', background: '#fffbeb', fontSize: 13, color: '#92400e' }}>
            ⚠ <b>Cancellation pending at the courier.</b> Their status flip takes a few minutes.
            Do not treat this delivery as stopped until it's confirmed.
            <div style={{ marginTop: 8 }}>
              <button type="button" onClick={() => void doVerifyCancel()} disabled={verifying}
                style={{ border: '1px solid #d97706', background: '#d97706', color: '#fff', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {verifying ? 'Checking…' : '↻ Verify cancellation'}
              </button>
            </div>
          </div>
        )}
        {s.status === 'delivered' && s.courier_pod_pdf && (
          <button type="button" style={btn}
                  title="Download the courier's own POD PDF (Roadpas delivery receipt)"
                  onClick={() => api.openShipmentCourierPod(shipmentId).catch(() => { alert('Could not open the courier document. Please try again.') })}>
            <Download size={14} /> Courier POD (PDF)
          </button>
        )}
        {!isCancelledState && needsReconcile && (
          <button type="button" style={btn} disabled={reconciling}
                  title="e-Courier assigns the order number a moment after creation — this finds and links it so cancel/track work"
                  onClick={doReconcile}>
            {reconciling ? 'Searching courier…' : 'Get courier order #'}
          </button>
        )}
        {canCancel && canResendCancel && (
          <button type="button" style={{ ...btn, color: '#dc2626', borderColor: '#dc262655' }}
                  title="This shipment is cancelled in OwlRx, but the courier may still have it live — re-send the cancel and verify at the courier"
                  onClick={doCancel} disabled={cancelling}>
            <XCircle size={14} /> {cancelling ? 'Verifying at courier…' : 'Re-send cancel to courier'}
          </button>
        )}
        {canCancel && canCancelStatus && (
          <button type="button" style={{ ...btn, color: '#dc2626', borderColor: '#dc262655' }}
                  onClick={doCancel} disabled={cancelling}>
            <XCircle size={14} /> {cancelling ? 'Cancelling…' : 'Cancel shipment'}
          </button>
        )}
        {isAdmin && (['created', 'label_purchased', 'pending_confirmation', 'cancel_pending'].includes(s.status)
                     || (s.status === 'cancelled' && !!s.carrier_shipment_id && /^[0-9]+$/.test(s.carrier_shipment_id))) && (
          <button type="button" style={{ ...btn, color: '#b91c1c', borderColor: '#b91c1c55' }}
                  title={s.status === 'cancelled'
                    ? 'ADMIN: this is cancelled in OwlRx but may still be live at the courier — delete it there and verify'
                    : 'ADMIN: delete this not-yet-shipped order at the courier and verify the result'}
                  onClick={doDelete} disabled={deleting}>
            <XCircle size={14} /> {deleting ? 'Deleting at courier…'
              : s.status === 'cancelled' ? 'Delete at courier (admin)' : 'Delete order (admin)'}
          </button>
        )}
      </div>
      {reconcileMsg && <div style={{ fontSize: 12.5, margin: '6px 0', color: 'var(--color-text-muted)' }}>{reconcileMsg}</div>}

      {/* Deliver to */}
      <section style={sect}>
        <div style={sectHdr}>Deliver to</div>
        {/* D-SOC-6: which site of care, and whether this went somewhere other than the
            patient's own address. Without the flag a redirected package reads as a data
            error — the drawer used to show the PATIENT's address here while the box was
            on its way to a facility in another city. */}
        {s.site_of_care_facility && (
          <div style={{ fontSize: 11.5, marginBottom: 4 }}>
            <span style={{ padding: '2px 7px', borderRadius: 10, background: 'var(--color-primary-soft, #e6f4f4)', color: 'var(--color-primary, #0e7c7b)', fontWeight: 600 }}>
              🏥 Site of care: {s.site_of_care_facility}
            </span>
          </div>
        )}
        {s.destination_is_redirect && !s.site_of_care_facility && (
          <div style={{ fontSize: 11.5, marginBottom: 4, color: 'var(--color-warning-text, #b45309)' }}>
            ↪ Not the patient's address on file.
          </div>
        )}
        {s.recipient_name || s.recipient_line1 ? (
          <>
            {s.recipient_name && <div style={{ fontWeight: 600 }}>{s.recipient_name}</div>}
            {s.recipient_line1 && <div>{s.recipient_line1}</div>}
            {(s.recipient_city || s.recipient_state || s.recipient_zip) && (
              <div>{[s.recipient_city, s.recipient_state].filter(Boolean).join(', ')}{s.recipient_zip ? ` ${s.recipient_zip}` : ''}</div>
            )}
            {s.recipient_phone && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{s.recipient_phone}</div>}
          </>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>No recipient on file.</div>
        )}
      </section>

      {/* Carrier / order identifiers + financials */}
      <section style={sect}>
        <div style={sectHdr}>Carrier</div>
        <KV label="Carrier order #" value={s.carrier_shipment_id} />
        <KV label="Created by" value={
          isImport
            ? <span>Imported into OwlRx{s.created_by ? ` by ${s.created_by}` : ''}<span style={{ color: 'var(--color-text-muted)' }}> · order entered at courier</span></span>
            : (s.created_by || <span style={{ color: 'var(--color-text-muted)' }}>—</span>)
        } />
        {s.carrier_reference && (() => {
          const refs = (s as any).rx_refs as { rx_number: string; rx_id: string }[] | undefined
          const lk = { color: 'var(--color-primary, #2563eb)', textDecoration: 'none', fontWeight: 600 } as const
          if (refs && refs.length > 0) return (
            <KV label="Rx (courier Ref#)" value={
              <span>{refs.map((rf, i) => (
                <span key={rf.rx_id}>
                  {i > 0 && <span style={{ color: 'var(--color-text-muted)' }}>, </span>}
                  <a href={`/rx/${rf.rx_id}?from=shipment&sid=${shipmentId}`} style={lk} title={`Open Rx ${rf.rx_number}`}>{rf.rx_number}</a>
                </span>
              ))}</span>
            } />
          )
          return <KV label="Rx (courier Ref#)" value={s.carrier_reference} />
        })()}
        {s.tracking_number && s.tracking_number !== s.carrier_shipment_id && (
          <KV label="Tracking #" value={s.tracking_number} />
        )}
        <KV label="Service" value={s.carrier_service_code || s.service_level} />
        {canSeeCost && s.carrier_amount != null && <KV label="Carrier charge" value={`$${s.carrier_amount.toFixed(2)}`} />}
        {s.carrier_status_text && <KV label="Carrier status" value={s.carrier_status_text} />}
      </section>

      {/* Handling */}
      {(s.cold_chain || s.require_signature || s.adult_signature || s.saturday_delivery || s.special_instructions) && (
        <section style={sect}>
          <div style={sectHdr}>Handling</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {s.cold_chain && <span style={chip}>❄ Cold chain</span>}
            {s.cold_chain && labelCfg?.show_refrigerate_banner && <span style={{ ...chip, background: '#1d4ed8', color: '#fff' }}>❄ REFRIGERATE banner prints</span>}
            {s.require_signature && <span style={chip}>✎ Signature</span>}
            {s.adult_signature && <span style={chip}>21+ Adult sig</span>}
            {s.saturday_delivery && <span style={chip}>Sat delivery</span>}
          </div>
          {s.special_instructions && (
            <div style={{ marginTop: 8, fontSize: 13, padding: '6px 10px', background: 'var(--color-bg, #f8fafc)', borderRadius: 6 }}>
              {s.special_instructions}
            </div>
          )}
        </section>
      )}

      {/* Label tags configured in Shipping settings — what will print on this label */}
      {labelCfg && ((labelCfg.custom_elements?.length ?? 0) > 0 || labelCfg.show_consult_message) && (
        <section style={sect}>
          <div style={sectHdr}>Label tags (from Shipping settings)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(labelCfg.custom_elements ?? []).map(el => (
              <div key={el.id} style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <span style={{ ...chip, fontSize: 10, textTransform: 'uppercase' }}>{el.region}{el.emphasis !== 'normal' ? ` · ${el.emphasis}` : ''}</span>
                <span style={{ fontWeight: el.emphasis === 'normal' ? 400 : 700 }}>{el.text}</span>
              </div>
            ))}
            {labelCfg.show_consult_message && labelCfg.consult_message && (
              <div style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <span style={{ ...chip, fontSize: 10 }}>FOOTER</span>
                <span>{labelCfg.consult_message}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Timeline */}
      {data.events.length > 0 && (
        <section style={sect}>
          <div style={sectHdr}>Activity</div>
          <VerticalTimeline events={data.events} currentStatus={s.status} />
        </section>
      )}

      {/* Proof of Delivery — signature renders by type (vector strokes vs image) */}
      {(s.pod_image_data || s.pod_signature_name) && (
        <section style={{ ...sect }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-muted)', marginBottom: 8 }}>
            Proof of Delivery
          </div>
          <div>
            <PodSignature data={s.pod_image_data} type={s.pod_image_mime} />
            {s.pod_image_data && s.courier_pod_pdf && (
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                Stroke capture preview — the carrier's signature image is on the Courier POD (PDF).
              </div>
            )}
          </div>
          {s.pod_signature_name && (
            <div style={{ fontSize: 13, marginTop: 8 }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Received: </span>
              <span style={{ fontWeight: 500 }}>{s.pod_signature_name}</span>
              {s.delivered_at ? ` · ${new Date(s.delivered_at).toLocaleString()}` : ''}
            </div>
          )}
        </section>
      )}

      {/* POD-4 (Patrick 2026-08-02): delivery photos as a thumbnail grid, tap to expand */}
      {(() => {
        const photos = (data.documents || []).filter(d => (d.content_type || '').startsWith('image/') || d.doc_type === 'photo')
        if (photos.length === 0) return null
        return (
          <section style={{ ...sect }}>
            <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              Delivery photos ({photos.length})
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {photos.map(pp => {
                const src = pp.data_base64 ? `data:${pp.content_type || 'image/jpeg'};base64,${pp.data_base64}` : undefined
                if (!src) return null
                return (
                  <button key={pp.id} type="button" onClick={() => setPhotoZoom(src)} aria-label="Expand delivery photo"
                    style={{ padding: 0, border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden', cursor: 'zoom-in', background: '#fff' }}>
                    <img src={src} alt="delivery photo" style={{ width: '100%', display: 'block', aspectRatio: '4 / 3', objectFit: 'cover' }} />
                  </button>
                )
              })}
            </div>
          </section>
        )
      })()}

      {/* POD Documents — POD PDF + photo files as download rows (photos shown in the grid above) */}
      {data.documents && data.documents.length > 0 && (
        <section style={{ ...sect }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-muted)', marginBottom: 8 }}>
            Documents ({data.documents.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.documents.map(doc => (
              <PodDocumentItem key={doc.id} doc={doc} />
            ))}
          </div>
        </section>
      )}

      {/* Delivery documents — deep-link to the carrier portal (signature, house &
          package photos, GPS). Opens the exact order; user logs in to view. */}
      {s.tracker_url && (
        <section style={{ ...sect }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-muted)', marginBottom: 8 }}>
            Delivery Documents
          </div>
          <a
            href={s.tracker_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: 'var(--color-primary, #0369a1)', color: '#fff', textDecoration: 'none',
            }}
          >
            View delivery photos & documents ↗
          </a>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 6 }}>
            Opens order {s.carrier_shipment_id} on the carrier portal. Live tracking shows without signing in; the full documents (signature, photos of the house & package location, GPS) require a carrier-portal login.
          </div>
        </section>
      )}

      {/* Metadata */}
      <section style={{ ...sect, fontSize: 12, color: 'var(--color-text-muted)' }}>
        {s.shipped_at && (
          isImport
            ? <div>Order created {new Date(s.shipped_at).toLocaleString()}</div>
            : <div>Created {new Date(s.shipped_at).toLocaleString()}{s.created_by ? ` by ${s.created_by}` : ''}</div>
        )}
        {isImport && s.imported_from_courier_at && (
          <div>Imported from carrier {new Date(s.imported_from_courier_at).toLocaleString()}{s.created_by ? ` by ${s.created_by}` : ''}</div>
        )}
        {s.picked_up_at && <div>Picked up {new Date(s.picked_up_at).toLocaleString()}</div>}
        {s.delivered_at && <div>Delivered {new Date(s.delivered_at).toLocaleString()}{s.pod_signature_name ? ` · signed ${s.pod_signature_name}` : ''}</div>}
        {s.last_tracked_at && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><RefreshCw size={10} /> Last tracked {new Date(s.last_tracked_at).toLocaleString()}</div>}
      </section>
      {photoZoom && (
        <div onClick={() => setPhotoZoom(null)} role="dialog" aria-label="Delivery photo" aria-modal="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, cursor: 'zoom-out', padding: 16 }}>
          <img src={photoZoom} alt="delivery photo" style={{ maxWidth: '92%', maxHeight: '92%', borderRadius: 8 }} />
        </div>
      )}
    </>
  )
}

function PodDocumentItem({ doc }: { doc: api.ShipmentPodDocument }) {
  const isPdf = (doc.content_type || '').includes('pdf') || doc.doc_type === 'pod_pdf'
  const href = doc.data_base64 ? `data:${doc.content_type || 'application/octet-stream'};base64,${doc.data_base64}` : undefined
  const label = doc.file_name || (isPdf ? 'POD.pdf' : doc.doc_type)
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', fontSize: 12, gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
            ID {doc.id.slice(0, 8)}{doc.captured_at ? ` · ${new Date(doc.captured_at).toLocaleDateString()}` : ''}
          </div>
        </div>
        {href && (
          <a href={href} download={label} style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: 'var(--color-primary, #0369a1)', textDecoration: 'none' }}>
            {isPdf ? 'Download PDF' : 'Download'}
          </a>
        )}
      </div>
    </div>
  )
}

function PodSignature({ data, type }: { data: string | null; type: string | null }) {
  if (!data) return null

  if (type === 'vector') {
    // 8-bit byte pairs (2 hex X, 2 hex Y); Y >= 0x80 = pen lift (new stroke).
    // Verified against real Roadpas receipts 2026-07-17.
    const strokes: Array<Array<[number, number]>> = []
    let cur: Array<[number, number]> = []
    for (let i = 0; i + 4 <= data.length; i += 4) {
      const x = parseInt(data.slice(i, i + 2), 16)
      const y = parseInt(data.slice(i + 2, i + 4), 16)
      if (Number.isNaN(x) || Number.isNaN(y)) continue
      if (y >= 0x80) { if (cur.length > 1) strokes.push(cur); cur = [] }
      else cur.push([x, y])
    }
    if (cur.length > 1) strokes.push(cur)
    if (strokes.length === 0) return null
    const all = strokes.flat()
    const minX = Math.min(...all.map(p => p[0])), maxX = Math.max(...all.map(p => p[0]))
    const minY = Math.min(...all.map(p => p[1])), maxY = Math.max(...all.map(p => p[1]))
    const w = Math.max(1, maxX - minX + 12), h = Math.max(1, maxY - minY + 12)
    return (
      <svg viewBox={`0 0 ${w} ${h}`} style={{ maxWidth: 340, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
        {strokes.map((st, si) => (
          <polyline key={si}
            points={st.map(p => `${p[0] - minX + 6},${p[1] - minY + 6}`).join(' ')}
            fill="none" stroke="#111" strokeWidth={2} vectorEffect="non-scaling-stroke"
            strokeLinecap="round" strokeLinejoin="round" />
        ))}
      </svg>
    )
  }

  // Image format (JPEG/PNG base64). The carrier may return a "Signature Not
  // Captured" placeholder for no-signature deliveries — show it but labeled.
  return (
    <img
      src={`data:${type || 'image/jpeg'};base64,${data}`}
      alt="Proof of delivery signature"
      style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: 8, background: '#fff', display: 'block' }}
    />
  )
}

function KV({ label, value }: { label: string; value: ReactNode }) {
  if (!value) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const tone = ({ created: '#6b7280', label_purchased: '#0369a1', in_transit: '#0369a1',
    out_for_delivery: '#a16207', delivered: '#15803d', exception: '#dc2626', cancelled: '#6b7280' } as Record<string, string>)[status] ?? '#6b7280'
  const label = ({ created: 'Created', label_purchased: 'Label purchased', in_transit: 'In transit',
    out_for_delivery: 'Out for delivery', delivered: 'Delivered', exception: 'Exception', cancelled: 'Cancelled' } as Record<string, string>)[status] ?? status
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: '1px solid', background: `${tone}1a`, color: tone, borderColor: `${tone}55` }}>{label}</span>
}

function VerticalTimeline({ events, currentStatus }: { events: api.ShipmentDetailEvent[]; currentStatus: string }) {
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {events.map((e, i) => {
        const isLast = i === events.length - 1
        const isCurrent = e.status === currentStatus
        const color = stageColor(e.status)
        return (
          <li key={i} style={{ display: 'flex', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: 18, height: 18, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: color, border: `2px solid ${color}`, flexShrink: 0 }}>
                {iconForStage(e.status)}
              </div>
              {!isLast && <div style={{ width: 2, flex: 1, background: 'var(--color-border)', marginTop: 2, minHeight: 14 }} />}
            </div>
            <div style={{ paddingBottom: isLast ? 0 : 14, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: isCurrent ? 700 : 500 }}>{e.description || e.status}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                {e.location ? `${e.location} · ` : ''}{new Date(e.occurred_at).toLocaleString()}
              </div>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
function stageColor(status: string) {
  return ({ created: '#6b7280', label_purchased: '#0369a1', in_transit: '#0369a1',
    out_for_delivery: '#a16207', delivered: '#15803d', exception: '#dc2626', cancelled: '#6b7280' } as Record<string,string>)[status] ?? '#6b7280'
}
function iconForStage(status: string) {
  const sz = 10; const col = '#fff'
  if (status === 'delivered') return <CheckCircle2 size={sz} color={col} />
  if (status === 'out_for_delivery') return <Truck size={sz} color={col} />
  if (status === 'in_transit' || status === 'label_purchased') return <Send size={sz} color={col} />
  return <Circle size={sz} color={col} />
}

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 50 }
const drawer: React.CSSProperties = { position: 'fixed', top: 0, right: 0, height: '100vh', width: 'min(520px, 92vw)', background: 'var(--color-surface)', borderLeft: '1px solid var(--color-border)', boxShadow: '-8px 0 24px rgba(0,0,0,.12)', zIndex: 51, overflowY: 'auto', display: 'flex', flexDirection: 'column' }
const hdr: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, background: 'var(--color-surface)', zIndex: 1 }
const closeBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: '1px solid var(--color-border)', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: 'var(--color-text-muted)' }
const body: React.CSSProperties = { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }
const sect: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 }
const sectHdr: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 4 }
const errBox: React.CSSProperties = { margin: 16, padding: '8px 12px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, whiteSpace: 'pre-line' }
const actionRow: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap' }
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer' }
const chip: React.CSSProperties = { fontSize: 11, padding: '2px 8px', border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-bg, #f8fafc)' }
const typeTag: React.CSSProperties = { fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }
